import React, { useState } from 'react';
import { Plus, Edit3, Trash2 } from 'lucide-react';
import { authAxios } from '@/lib/api';
import BlogEditor from './BlogEditor';

function BlogPostRow({ post, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-4 flex items-center gap-4" data-testid={`admin-blog-${post.slug}`}>
      {post.cover_image && <img src={post.cover_image} alt="" className="w-20 h-14 rounded object-cover flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-sm font-bold text-earth-dark truncate">{post.title}</h3>
          {post.published ? (
            <span className="text-[8px] uppercase tracking-wider text-green-600 bg-green-50 px-2 py-0.5 rounded font-sans font-semibold">Published</span>
          ) : (
            <span className="text-[8px] uppercase tracking-wider text-earth/40 bg-earth/5 px-2 py-0.5 rounded font-sans font-semibold">Draft</span>
          )}
        </div>
        <p className="text-xs text-earth/50 font-sans truncate mt-0.5">{post.excerpt}</p>
        <div className="flex gap-2 mt-1">
          {post.tags?.slice(0, 3).map(t => (
            <span key={t} className="text-[8px] uppercase tracking-wider text-saffron bg-saffron/5 px-1.5 py-0.5 rounded font-sans">{t}</span>
          ))}
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={onEdit} className="p-2 text-saffron hover:bg-saffron/5 rounded transition-colors" data-testid={`edit-blog-${post.slug}`}><Edit3 size={14} /></button>
        <button onClick={onDelete} className="p-2 text-chilli/50 hover:bg-chilli/5 rounded transition-colors" data-testid={`delete-blog-${post.slug}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export default function BlogTab({ posts, onRefresh }) {
  // null = closed, { post: null } = new post, { post } = editing
  const [editor, setEditor] = useState(null);

  const handleSave = async (data) => {
    if (editor.post) {
      await authAxios.put(`/api/blog/${editor.post.slug}`, data);
    } else {
      await authAxios.post('/api/blog', data);
    }
    setEditor(null);
    onRefresh();
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this post?')) return;
    await authAxios.delete(`/api/blog/${slug}`);
    onRefresh();
  };

  return (
    <div className="space-y-4" data-testid="blog-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-earth/50 font-sans">{posts.length} posts</p>
        <button onClick={() => setEditor({ post: null })} className="flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-xs px-4 py-2.5 rounded-md hover:bg-saffron-dark transition-colors" data-testid="add-blog-btn">
          <Plus size={14} /> New Post
        </button>
      </div>

      {editor && (
        <BlogEditor key={editor.post?.slug || 'new'} post={editor.post} onSave={handleSave} onCancel={() => setEditor(null)} />
      )}

      <div className="space-y-3">
        {posts.map(post => (
          <BlogPostRow key={post.slug} post={post} onEdit={() => setEditor({ post })} onDelete={() => handleDelete(post.slug)} />
        ))}
      </div>
    </div>
  );
}
